import React from 'react';
import { Card, Table } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  analyzeMvx,
  indexBoundaryLabels,
  indexSegments,
  markerBar,
  MvxBarSegment,
  MvxZone,
  Specimen,
} from '../../models/MetabolicVulnerability';
import { PanelField } from '../../models/PanelField';
import { MvxRangeChart, MvxChartBand } from '../charts/MvxRangeChart';
import { formatCollectionDateShort } from '../../helpers/previousOnlyMarkers';
import { MvxSeverityBar, SHORT_TARGET_LABEL } from '../charts/MvxSeverityBar';
import { MvxOptimalBar } from '../charts/MvxOptimalBar';
import { inputsFromBloodwork, previousInputsFromBloodwork } from './metabolic-vulnerability-insights';
import { MUTED_PALETTE } from '../charts/chartPalette';

/**
 * MVX Plus Results panel. Every marker in the section as a bar graph, in the
 * same `labsmartz-chart-table` layout the app uses for all other blood markers
 * (name + value on the left, a reference-range chart on the right), rendered via
 * `MvxRangeChart`. The 3 index scores (MVX/IVX/MMX) and the 6 factors all appear
 * as rows. Interpretation lives in the Insights panel — this is the numbers.
 */
interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  specimen?: Specimen;
}

const FILL: Record<MvxZone, string> = MUTED_PALETTE;

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));

// Zone → the app's band-header label ("Alarm / < Lab / Lab / Optimal / …"),
// with orange split into "< Lab" (low side of optimal) vs "> Lab" (high side).
const bandLabel = (zone: MvxZone, isLowSide: boolean): string => {
  switch (zone) {
    case 'green': return 'Optimal';
    case 'yellow': return 'Lab';
    case 'orange': return isLowSide ? '< Lab' : '> Lab';
    case 'red': return 'Alarm';
    default: return '';
  }
};

// Decimals for a marker, off the entry field that captures it (scores 0, GlycA
// 0, small HDL-P 1). Only the DERIVED green-band gradations read it, and they
// take it as a FLOOR of one decimal (see severityTicks) — a whole-number score
// bar still needs tenths for its gradations to step evenly. Stored band
// boundaries always print as-is.
const precisionOf = (id: string): number => PanelField.MVX_FIELDS.find(f => f.id === id)?.precision ?? 0;

// The four increasing-severity bars (MVX/IVX/MMX indices + GlycA) render RDW-style.
const SEVERITY_LABELS = ['Optimal', 'Lab', '> Lab', 'Alarm'];
// Small HDL-P is higher-better: the same bar mirrored, green Optimal on the right.
const SEVERITY_LABELS_REV = ['Alarm', '< Lab', 'Lab', 'Optimal'];
const severityBoundaries = (segs: MvxBarSegment[]): number[] => [
  segs[0].from,
  segs[0].to,
  segs[1].to,
  segs[2].to,
  segs[3].to,
];

const bandsFrom = (segments: MvxBarSegment[]): MvxChartBand[] => {
  const green = segments.find(s => s.zone === 'green');
  const greenFrom = green ? green.from : Infinity;
  return segments.map(s => ({
    color: FILL[s.zone],
    from: s.from,
    to: s.to,
    label: bandLabel(s.zone, s.to <= greenFrom + 1e-9),
  }));
};

// The band boundaries (excluding the outer padding edges) become the tick labels.
const ticksFrom = (segments: MvxBarSegment[], domLo: number, domHi: number): number[] => {
  const eps = (domHi - domLo) * 1e-6;
  const vals = new Set<number>();
  segments.forEach(s => {
    vals.add(s.from);
    vals.add(s.to);
  });
  return Array.from(vals)
    .filter(v => v > domLo + eps && v < domHi - eps)
    .sort((a, b) => a - b);
};

interface Row {
  id: string;
  name: string;
  valueLabel: string;
  domLo: number;
  domHi: number;
  bands: MvxChartBand[];
  ticks: number[];
  value: number;
  /** The previous test's value, when it has one — the blue comparison drop. */
  previousValue?: number;
  /** Present for the RDW-style severity bars (indices + GlycA, + mirrored sHDL-P). */
  severity?: { boundaries: number[]; labelBoundaries?: number[]; labels: string[]; reversed?: boolean; softenFrom?: number };
  /** Present for the symmetric optimal-in-the-middle factor bars (citrate, BCAAs). */
  optimal?: { optimal: [number, number]; reference: [number, number]; severe: [number, number] };
}

export class MvxResultsPanel extends React.Component<Props> {
  static shouldRender(ba: BloodworkAnalysis | undefined): boolean {
    return analyzeMvx(inputsFromBloodwork(ba)).anyEntered;
  }

  render() {
    const { bloodworkAnalysis, specimen } = this.props;
    const result = analyzeMvx(inputsFromBloodwork(bloodworkAnalysis), specimen);
    if (!result.anyEntered) return null;

    // The previous test's values, for the blue comparison drop. Analyzed the
    // same way so an index/factor that the previous test did not measure simply
    // has no drop, rather than plotting a zero.
    const previous = analyzeMvx(previousInputsFromBloodwork(bloodworkAnalysis), specimen);
    const previousIndex = (id: string) => previous.indices.find(i => i.id === id);
    const previousFactor = (id: string) => previous.factors.find(f => f.id === id);
    const was = (r?: { value: number; hasValue: boolean }) => (r && r.hasValue ? r.value : undefined);

    const enteredIndices = result.indices.filter(i => i.hasValue);
    // Sub-index scores are not capped at 100 (MMX seen at 110); share one scale.
    const subMax = Math.max(120, ...enteredIndices.map(i => Math.ceil(i.value * 1.05)));

    const built: Row[] = [];
    const rows: Row[] = [];
    enteredIndices.forEach(ix => {
      const segs = indexSegments(ix, specimen, subMax);
      built.push({
        id: ix.id,
        name: ix.short,
        valueLabel: `${fmt(ix.value)} score`,
        domLo: 0,
        domHi: subMax,
        bands: bandsFrom(segs),
        ticks: ticksFrom(segs, 0, subMax),
        value: ix.value,
        previousValue: was(previousIndex(ix.id)),
        severity: {
          boundaries: severityBoundaries(segs),
          // MMX: green fades to yellow between 45 and 49.
          softenFrom: ix.softenFrom,
          // An index band ENDS one below where its geometry does: Optimal runs
          // to 34, not 35. The factors need no such translation.
          labelBoundaries: indexBoundaryLabels(segs),
          labels: SEVERITY_LABELS,
        },
      });
    });
    result.factors
      .filter(f => f.hasValue)
      .forEach(f => {
        const bar = markerBar(f, f.value);
        built.push({
          id: f.id,
          name: f.short,
          valueLabel: `${fmt(f.value)} ${f.units}`,
          domLo: bar.domLo,
          domHi: bar.domHi,
          bands: bandsFrom(bar.segments),
          ticks: ticksFrom(bar.segments, bar.domLo, bar.domHi),
          value: f.value,
          previousValue: was(previousFactor(f.id)),
          // GlycA (lower-better) and small HDL-P (higher-better, mirrored) both
          // have the four-band shape — RDW-style, GlycA green-left, sHDL-P green-right.
          severity:
            f.direction === 'lowerBetter'
              ? {
                  boundaries: severityBoundaries(bar.segments),
                  labels: SEVERITY_LABELS,
                  // GlycA: green fades to yellow between 260 and 300.
                  softenFrom: f.softenFrom,
                }
              : f.direction === 'higherBetter'
              ? { boundaries: severityBoundaries(bar.segments), labels: SEVERITY_LABELS_REV, reversed: true }
              : undefined,
          // The optimal-in-the-middle factors (citrate, BCAAs) get the symmetric bar.
          optimal:
            f.direction === 'bandOptimal'
              ? { optimal: f.optimal, reference: f.reference, severe: f.severe }
              : undefined,
        });
      });

    // LABCORP'S OWN ORDER, which is hierarchical rather than alphabetical:
    // each sub-index sits directly above the factors that compute it (their
    // report #123601, and Dr. Schmidt's "IVX is GlycA + small HDL-P, MMX is
    // citrate + the three BCAAs").
    //
    // A practitioner reads this panel with the printed report beside it, so
    // running a finger down both has to work. It also shows what drives
    // what: a high IVX used to give no clue that only two factors feed it.
    // (Maryann, 2026-10-15, from their sample report.)
    const ORDER = ['mvx', 'ivx', 'glycA', 'sHdlP', 'mmx', 'citrate', 'valine', 'leucine', 'isoleucine'];
    ORDER.forEach(id => {
      const row = built.find(r => r.id === id);
      if (row) rows.push(row);
    });

    // Both collection dates for the hover boxes, formatted once.
    const dates = {
      currentDate: formatCollectionDateShort(bloodworkAnalysis.bloodwork?.collectionDate),
      previousDate: formatCollectionDateShort(bloodworkAnalysis.previousBloodwork?.collectionDate),
    };

    // The hairlines follow the hierarchy: the overall score, then each
    // sub-index with the factors that make it.
    const GROUPS: string[][] = [
      ['mvx'],
      ['ivx', 'glycA', 'sHdlP'],
      ['mmx', 'citrate', 'valine', 'leucine', 'isoleucine'],
    ];
    /**
     * Which rows carry the band names.
     *
     * The three shapes of bar no longer sit in contiguous blocks, so the
     * legend follows the SHAPE rather than the group: once for the
     * lower-better bars, on the first of them, and once per group for the
     * windows. Small HDL-P and Citrate both get one because Small HDL-P's
     * ceilings coincide, so its row cannot show the upper Lab name and
     * Citrate's complete set stays in view.
     */
    const leads = new Set<number>();
    GROUPS.forEach(g => {
      // One legend per SHAPE per group: the hairlines mean a group can no
      // longer read a legend that sits above the rule. GlycA needs none of
      // its own, since IVX is the same shape and sits directly above it.
      [
        rows.findIndex(r => g.includes(r.id) && r.severity),
        rows.findIndex(r => g.includes(r.id) && r.optimal),
      ].forEach(i => {
        if (i >= 0) leads.add(i);
      });
    });
    /** The last row of each group, which is where the hairline goes. */
    const closes = new Set(
      GROUPS.slice(0, -1)
        .map(g => {
          const inGroup = rows.map((r, i) => (g.includes(r.id) ? i : -1)).filter(i => i >= 0);
          return inGroup.length ? inGroup[inGroup.length - 1] : -1;
        })
        .filter(i => i >= 0),
    );
    /**
     * The hairline between groups, as its own row.
     *
     * INSET TO THE CHART COLUMN, not run across the whole table: what it
     * divides is the bars, and a rule under the marker names reads as a
     * table section break instead. It is NOT shortened to the lab range
     * either, which was the other idea on the table: these bars are
     * normalized, so lab min and lab max sit at different x on each row, and
     * a rule spanning only that stretch would begin and end somewhere that
     * means one thing above it and another below. Structural lines should
     * not look like they are pointing at a value.
     *
     * Air on BOTH sides: 13px above, 15px below. The first pass had none
     * underneath, which left the rule sitting on top of the next group's
     * band names (Maryann, 2026-10-15). A little more below than above,
     * because what follows is a line of type while what precedes it is a row
     * of numbers with their own descender space.
     *
     * INSIDE the chart cell, not a row of its own. A separator row has to
     * put something in the marker-name column, and this table sizes its two
     * columns against each other from every row it has: an empty name cell
     * let that column win the negotiation and the bar graphs lost a chunk of
     * their width (Maryann, 2026-10-15, and the same trap the comment on
     * td.ref-range-category describes). Nine rows in, nine rows out.
     *
     * A div rather than a border on the cell, because index.css sets
     * `padding: 0 !important` on every cell here, so the air either side of
     * the line has to be a margin.
     */
    // 10px under the line, down from 15 over three passes on 2026-10-16,
    // reading the panel each time. That gap is the air above the IVX and MMX
    // label rows, which open the two groups this line closes.
    //
    // 13px above and 10px below, so the line sits NEARER the group it opens
    // than the one it closes. Deliberate, and the opposite of where this
    // started: a labelled row heads what follows it, so the line belongs
    // with the rows under it.
    const groupRule = <div style={{ borderTop: '1px solid #e4dfd8', margin: '13px 0 10px' }} />;

    // NO `overflow: hidden` on the Card, which this panel alone used to carry
    // and which is why this panel alone showed white at its top corners. The
    // header's background normally runs under the card's 5px border and is
    // covered by it; clipping to the padding box cuts it back to the inner
    // curve instead and the card's white shows through between the two
    // (Maryann, 2026-10-15). Nothing needs the clip: the charts set their own
    // overflow and the table sits inside the body.
    return (
      <Card className="evaluationPanel" id="mvx-results" style={{ width: 760, maxWidth: '100%' }}>
        <Card.Header className="evaluationPanelHeading">MVX Plus Results</Card.Header>
        <Card.Body style={{ margin: 0, width: '100%' }}>
          <Table className="labsmartz-chart-table">
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id}>
                  <td className="text-nowrap ref-range-category">
                    {row.name}
                    <br />
                    <span className="ref-range-category-value">{row.valueLabel}</span>
                  </td>
                  <td>
                    {row.severity ? (
                      <MvxSeverityBar
                        boundaries={row.severity.boundaries}
                        labelBoundaries={row.severity.labelBoundaries}
                        labels={row.severity.labels}
                        softenFrom={row.severity.softenFrom}
                        reversed={row.severity.reversed}
                        value={row.value}
                        previousValue={row.previousValue}
                        format={fmt}
                        precision={precisionOf(row.id)}
                        targetLabel={row.id === 'mvx' ? undefined : SHORT_TARGET_LABEL}
                        {...dates}
                        showLabels={leads.has(i)}
                      />
                    ) : row.optimal ? (
                      <MvxOptimalBar
                        optimal={row.optimal.optimal}
                        reference={row.optimal.reference}
                        severe={row.optimal.severe}
                        value={row.value}
                        previousValue={row.previousValue}
                        format={fmt}
                        showLabels={leads.has(i)}
                        {...dates}
                      />
                    ) : (
                      <MvxRangeChart
                        domLo={row.domLo}
                        domHi={row.domHi}
                        bands={row.bands}
                        ticks={row.ticks}
                        value={row.value}
                        format={fmt}
                        showLabels={false}
                      />
                    )}
                    {closes.has(i) ? groupRule : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  }
}

export default MvxResultsPanel;
